import type { TableSoundCue, TableSoundKind } from '@v2/contracts'
import type { Projection } from '@v2/sdk'

/**
 * Conservative tactile defaults, using only the viewer's portable scene.
 * Authored sounds take precedence. New tables/workspaces are quiet, and hidden
 * replacements never identify cards through sound names or occurrence ids.
 */
export function inferSceneSounds(previous: Projection | null, next: Projection, baseline = false): readonly TableSoundCue[] {
  if (baseline || !previous || previous.viewer !== next.viewer || previous.game.id !== next.game.id || next.revision <= previous.revision) return []
  const previousPieces = new Map(previous.scene.pieces.map(piece => [piece.id, piece]))
  const previousZones = new Set(previous.scene.zones.map(zone => zone.id))
  const zones = new Map(next.scene.zones.map(zone => [zone.id, zone]))
  const inferred = new Set<TableSoundKind>()
  for (const piece of next.scene.pieces) {
    if (!previousZones.has(piece.zone)) continue
    const before = previousPieces.get(piece.id)
    const zone = zones.get(piece.zone)
    if (!before) {
      if (piece.kind === 'card' && !piece.hidden && zone?.presentation === 'hand' && zone.owner === next.viewer) inferred.add('deal')
      continue
    }
    if (piece.motion?.shuffleKey !== undefined && before.motion?.shuffleKey !== undefined && piece.motion.shuffleKey !== before.motion.shuffleKey) inferred.add('shuffle')
    if (piece.kind === 'card' && before.hidden && !piece.hidden) inferred.add('reveal')
    const moved = piece.zone !== before.zone || JSON.stringify(piece.position) !== JSON.stringify(before.position) || piece.rotation !== before.rotation
    if (piece.kind === 'card' && moved) inferred.add('place')
    if ((piece.kind === 'pawn' || String(piece.kind) === 'cube') && moved) inferred.add('token')
    if (piece.kind === 'token' && before.count !== undefined && piece.count !== undefined && piece.count !== before.count) inferred.add('token')
  }
  const authored = new Set(next.scene.sounds?.map(cue => cue.sound))
  return [...inferred].filter(sound => !authored.has(sound)).map(sound => ({ id: `${next.game.id}:${next.revision}:${sound}`, sound }))
}
